import { NotificationType } from './notification.model';

/**
 * One row of the notification settings screen, as returned by BC_Corporate.
 *
 * `key` is the contract between the two sides: the server stores an enum but sends this string,
 * which matches {@link NotificationType} exactly, so a preference can be looked up by the type the
 * client already derives from a notification's foreign keys.
 */
export interface NotificationPreference {
  /** Numeric enum value as stored server-side. Sent back untouched on save. */
  notificationType: number;
  /** Matches a NotificationType value, e.g. 'leave_request'. */
  key: NotificationType;
  /** Show it in-app while a tab is open. */
  onScreen: boolean;
  /** Send it to the device as a web push. Recorded now, delivered once push is wired up. */
  push: boolean;
}

/** Human labels for the settings screen. Keyed by the same string the API returns. */
export const NOTIFICATION_TYPE_LABELS: Record<string, { title: string; description: string }> = {
  [NotificationType.LeaveRequest]: {
    title: 'Leave requests',
    description: 'Someone submits a leave request you need to approve.',
  },
  [NotificationType.LeaveApproved]: {
    title: 'Leave approved',
    description: 'One of your leave requests is approved.',
  },
  [NotificationType.LeaveRejected]: {
    title: 'Leave rejected',
    description: 'One of your leave requests is rejected.',
  },
  [NotificationType.MeetingMention]: {
    title: 'Note mentions',
    description: 'You are tagged in a meeting note.',
  },
  [NotificationType.Reaction]: {
    title: 'Reactions',
    description: 'Someone reacts to one of your notes.',
  },
  [NotificationType.Comment]: {
    title: 'Replies',
    description: 'Someone replies in a note thread you are part of.',
  },
  [NotificationType.Approval]: {
    title: 'Approvals',
    description: 'Something is waiting on your approval.',
  },
  [NotificationType.PendingChangeRequest]: {
    title: 'Change requests',
    description: 'A user proposes a field edit that needs an admin decision.',
  },
  [NotificationType.PendingChangeDecision]: {
    title: 'Change decisions',
    description: 'One of your proposed changes is approved or rejected.',
  },
  [NotificationType.System]: {
    title: 'System',
    description: 'Service messages and anything that does not fit the categories above.',
  },
};
